'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Users, Clock, Target, Lightbulb, Star, Eye, Brain, Heart, ArrowRight, UserCircle, UserPlus } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function IliskiYayilimiPage() {
  const cardPositions = [
    { position: 1, name: "Siz", description: "Sizin bu ilişkideki duruşunuz ve enerjiniz", person: "A" },
    { position: 2, name: "Karşı Taraf", description: "Diğer kişinin duruşu ve enerjisi", person: "B" },
    { position: 3, name: "İlişkinizin Temeli", description: "İlişkiyi birleştiren temel bağ", person: "shared" },
    { position: 4, name: "Geçmişten Gelen", description: "İlişkiyi etkileyen geçmiş olaylar", person: "shared" },
    { position: 5, name: "Olası Gelecek", description: "Mevcut yolda devam ederse nereye varır", person: "shared" },
    { position: 6, name: "Yapılması Gereken", description: "İlişkiyi geliştirmek için atılacak adım", person: "A" },
    { position: 7, name: "Sonuç", description: "Tüm faktörleri göz önünde bulundurarak sonuç", person: "shared" }
  ];

  const relationshipTypes = [
    {
      type: "Romantik İlişkiler",
      questions: [
        "Bu ilişki nasıl gelişecek?",
        "Aramızdaki bağ ne kadar güçlü?",
        "Bu kişiyle uzun vadeli olabilir miyiz?",
        "İlişkimizde neyi iyileştirmeli?"
      ],
      focus: "Duygusal bağ, uyum, gelecek potansiyeli"
    },
    {
      type: "Arkadaşlık",
      questions: [
        "Bu arkadaşlığımız nasıl?",
        "Birbirimize nasıl destek olabiliriz?",
        "Aramızdaki güven problemi var mı?",
        "Bu arkadaşlık bana ne katıyor?"
      ],
      focus: "Karşılıklı destek, güven, ortak değerler"
    },
    {
      type: "Aile İlişkileri",
      questions: [
        "Ailemle ilişkim nasıl düzelebilir?",
        "Bu aile dinamiği nasıl değişebilir?",
        "Geçmişten gelen sorunları nasıl çözebiliriz?",
        "Ailemle daha sağlıklı iletişim kurabilir miyim?"
      ],
      focus: "Aile dinamikleri, geçmiş etkiler, iyileşme"
    },
    {
      type: "İş İlişkileri",
      questions: [
        "İş ortağımla işbirliğimiz nasıl?",
        "Patronumla ilişkim nasıl iyileşir?",
        "Bu iş arkadaşlığı nasıl gelişir?",
        "Profesyonel sınırları nasıl korurum?"
      ],
      focus: "Profesyonel işbirliği, sınırlar, verimlilik"
    }
  ];

  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Okuma Teknikleri</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">İlişki Yayılımı</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/interpretation-techniques"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Okuma Teknikleri
          </Link>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-900/20 to-orange-900/20 rounded-xl p-8 mb-8 border border-yellow-500/20">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-xl flex items-center justify-center">
              <Users className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-100">İlişki Yayılımı</h1>
              <p className="text-yellow-300 font-medium">Orta Seviye • 7 Kart</p>
            </div>
          </div>
          <p className="text-lg text-slate-200 leading-relaxed">
            İki kişi veya durum arasındaki dinamiği derinlemesine inceler. Aşk ilişkileri, arkadaşlıklar,
            aile bağları ve iş ortaklıkları için ideal. İlişkilerin tüm yönlerini analiz eder.
          </p>
        </div>

        {/* Ana Bilgiler */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Clock className="h-6 w-6 text-yellow-400" />
              <h3 className="text-lg font-semibold text-slate-100">Süre</h3>
            </div>
            <p className="text-slate-300">15-30 dakika</p>
            <p className="text-slate-400 text-sm">Orta detay seviyesi</p>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Target className="h-6 w-6 text-yellow-400" />
              <h3 className="text-lg font-semibold text-slate-100">Amaç</h3>
            </div>
            <p className="text-slate-300">İlişki analizi</p>
            <p className="text-slate-400 text-sm">Dinamik değerlendirmesi</p>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Lightbulb className="h-6 w-6 text-yellow-400" />
              <h3 className="text-lg font-semibold text-slate-100">Seviye</h3>
            </div>
            <p className="text-slate-300">Orta</p>
            <p className="text-slate-400 text-sm">İlişki deneyimi yararlı</p>
          </div>
        </div>

        {/* İlişki Yayılımı Dizilimi */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">İlişki Yayılımı Dizilimi</h2>
          <div className="bg-slate-800 rounded-xl p-8 border border-slate-700">
            <div className="flex justify-center">
              <div className="grid grid-cols-3 gap-6 max-w-lg">
                {/* Row 1 */}
                <div className="bg-blue-600 rounded-lg p-4 text-center text-white font-bold flex flex-col items-center justify-center h-24">
                  1<br/><span className="text-xs">Siz</span>
                </div>
                <div className="bg-purple-600 rounded-lg p-4 text-center text-white font-bold flex flex-col items-center justify-center h-24">
                  3<br/><span className="text-xs">Temel</span>
                </div>
                <div className="bg-pink-600 rounded-lg p-4 text-center text-white font-bold flex flex-col items-center justify-center h-24">
                  2<br/><span className="text-xs">Karşı Taraf</span>
                </div>

                {/* Row 2 */}
                <div className="bg-orange-600 rounded-lg p-4 text-center text-white font-bold flex flex-col items-center justify-center h-24">
                  4<br/><span className="text-xs">Geçmiş</span>
                </div>
                <div className="bg-green-600 rounded-lg p-4 text-center text-white font-bold flex flex-col items-center justify-center h-24">
                  5<br/><span className="text-xs">Gelecek</span>
                </div>
                <div className="bg-teal-600 rounded-lg p-4 text-center text-white font-bold flex flex-col items-center justify-center h-24">
                  6<br/><span className="text-xs">Eylem</span>
                </div>

                {/* Row 3 */}
                <div className="col-start-2 bg-red-600 rounded-lg p-4 text-center text-white font-bold flex flex-col items-center justify-center h-24">
                  7<br/><span className="text-xs">Sonuç</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Kart Pozisyonları */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Kart Pozisyonları Detayı</h2>
          <div className="space-y-4">
            {cardPositions.map((card) => (
              <div key={card.position} className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                <div className="flex items-center gap-4 mb-2">
                  <div className="w-10 h-10 bg-yellow-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">{card.position}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {card.person === "A" && <UserCircle className="h-5 w-5 text-blue-400" />}
                    {card.person === "B" && <UserPlus className="h-5 w-5 text-pink-400" />}
                    {card.person === "shared" && <Users className="h-5 w-5 text-purple-400" />}
                    <h3 className="text-lg font-semibold text-slate-100">{card.name}</h3>
                  </div>
                </div>
                <p className="text-slate-300 text-sm ml-14">{card.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* İlişki Türleri */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Farklı İlişki Türleri</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {relationshipTypes.map((rel, index) => (
              <div key={index} className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                  <Heart className="h-5 w-5 text-yellow-400" />
                  {rel.type}
                </h3>
                <p className="text-slate-400 text-sm mb-4 italic">{rel.focus}</p>
                <div className="space-y-2">
                  {rel.questions.map((question, qIndex) => (
                    <div key={qIndex} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-slate-300 text-sm">{question}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Okuma Süreci */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Adım Adım Okuma Süreci</h2>
          <div className="space-y-6">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-yellow-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Hazırlık ve Odaklanma</h3>
                  <p className="text-slate-300 mb-2">
                    Kartları karıştırırken belirli bir kişiyi veya ilişkiyi düşünün.
                    İlişkinizle ilgili net bir soru formüle edin.
                  </p>
                  <div className="bg-slate-700 rounded-lg p-3">
                    <p className="text-slate-400 text-sm italic">
                      İpucu: "Bu ilişki nasıl gelişebilir?" gibi açık uçlu sorular daha iyi sonuç verir.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-yellow-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Kartları Yerleştirme</h3>
                  <p className="text-slate-300 mb-2">
                    İlk önce 1 ve 2 numaralı kartları (siz ve karşı taraf) yan yana yerleştirin.
                    Sonra ortalarına 3 numaralı kartı (temel) koyun.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-yellow-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Bireysel Analiz</h3>
                  <p className="text-slate-300 mb-2">
                    Her kartı önce bağımsız olarak yorumlayın. Özellikle 1 ve 2 numaralı kartları
                    kişilerin karakteristik özellikleri olarak değerlendirin.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-yellow-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">4</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">İlişki Dinamiği</h3>
                  <p className="text-slate-300">
                    3, 4, 5 numaralı kartları ilişkinin genel dinamiği olarak değerlendirin.
                    Son olarak 6 ve 7 numaralı kartlarla gelecek ve eylem planını analiz edin.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Yorumlama İpuçları */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yorumlama İpuçları</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <Brain className="h-5 w-5 text-blue-400" />
                Kişi Analizi
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <p className="text-slate-300 font-medium text-sm">1 ve 2 numaralı kartları karşılaştırın</p>
                    <p className="text-slate-400 text-xs">Benzerlikler mi var, zıtlıklar mı? Bu uyum mu çatışma mı yaratıyor?</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <p className="text-slate-300 font-medium text-sm">Kişiliklerdeki tamamlayıcılığı arayın</p>
                    <p className="text-slate-400 text-xs">Birinin eksikliği diğerinin güçlü yanı mı?</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <Heart className="h-5 w-5 text-purple-400" />
                İlişki Dinamiği
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <p className="text-slate-300 font-medium text-sm">3 numaralı kart çok önemli</p>
                    <p className="text-slate-400 text-xs">Bu ilişkiyi bir arada tutan temel enerjidir</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <p className="text-slate-300 font-medium text-sm">4-5-6 kartlarını zaman çizgisi olarak okuyun</p>
                    <p className="text-slate-400 text-xs">Geçmişten geleceğe doğru ilişkinin evrimini görün</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Yaygın Senaryolar */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaygın Kart Kombinasyonları</h2>
          <div className="space-y-4">
            <div className="bg-green-900/20 border border-green-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-green-200 mb-3">💚 Uyumlu İlişki İşaretleri</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-green-100 text-sm mb-1"><strong>1-2 pozisyonlarında:</strong></p>
                  <p className="text-green-200 text-xs">Tamamlayıcı kartlar (Kupa-Kılıç, Tılsım-Değnek)</p>
                </div>
                <div>
                  <p className="text-green-100 text-sm mb-1"><strong>3. pozisyonda:</strong></p>
                  <p className="text-green-200 text-xs">Güneş, Yıldız, İki Kupa, Aşıklar</p>
                </div>
              </div>
            </div>

            <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Dikkat Gereken İşaretler</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-amber-100 text-sm mb-1"><strong>1-2 pozisyonlarında:</strong></p>
                  <p className="text-amber-200 text-xs">Çatışan elementler, ters kartlar</p>
                </div>
                <div>
                  <p className="text-amber-100 text-sm mb-1"><strong>4. pozisyonda:</strong></p>
                  <p className="text-amber-200 text-xs">Kule, Ölüm, Beş Kılıç - çözülmemiş geçmiş</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/interpretation-techniques/celtic-cross"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: Celtic Cross
          </Link>

          <Link
            href="/about/tarot/interpretation-techniques/karar-verme"
            className="inline-flex items-center px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors"
          >
            Sonraki: Karar Verme
            <ArrowRight className="h-4 w-4 ml-2" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}